import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MessageSquare,
  Send,
  Paperclip,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Upload,
  Image,
} from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";

const ticketCategories = [
  { value: "card", label: "Card Issues" },
  { value: "account", label: "Account & Profile" },
  { value: "transaction", label: "Transaction Problems" },
  { value: "kyc", label: "KYC Verification" },
  { value: "technical", label: "Technical Support" },
  { value: "other", label: "Other" },
];

const priorityLevels = [
  { value: "low", label: "Low", color: "text-blue-600 dark:text-blue-400" },
  { value: "medium", label: "Medium", color: "text-yellow-600 dark:text-yellow-400" },
  { value: "high", label: "High", color: "text-orange-600 dark:text-orange-400" },
  { value: "urgent", label: "Urgent", color: "text-red-600 dark:text-red-400" },
];

// Sample existing tickets
const existingTickets = [
  {
    id: "TKT-2024-001",
    subject: "Card activation issue",
    category: "Card Issues",
    status: "open",
    priority: "high",
    date: "2024-03-15",
    lastUpdate: "2 hours ago",
  },
  {
    id: "TKT-2024-002",
    subject: "KYC verification pending",
    category: "KYC Verification",
    status: "in_progress",
    priority: "medium",
    date: "2024-03-14",
    lastUpdate: "1 day ago",
  },
  {
    id: "TKT-2024-003",
    subject: "Transaction declined",
    category: "Transaction Problems",
    status: "resolved",
    priority: "low",
    date: "2024-03-10",
    lastUpdate: "5 days ago",
  },
];

export default function SupportTicket() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("new"); // "new" or "existing"
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    subject: "",
    description: "",
    email: "",
    attachments: [],
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (files) => {
    // Filter only image files
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (imageFiles.length === 0) {
      alert('Please select only image files (JPG, PNG, GIF, WebP)');
      return;
    }
    
    const newFiles = imageFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newFiles]
    }));
  };

  const removeFile = (fileId) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(f => f.id !== fileId)
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    // All files will be images since we only accept images
    return <Image className="w-4 h-4" />;
  };

  const handleSubmit = () => {
    // Validate form
    if (!formData.category || !formData.subject || !formData.description) {
      return;
    }
    
    // Show success modal
    setShowSuccessModal(true);
    
    // Reset form after delay
    setTimeout(() => {
      setShowSuccessModal(false);
      setFormData({
        category: "",
        subject: "",
        description: "",
        email: "",
        attachments: [],
      });
      setActiveTab("existing");
    }, 3000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <Clock className="w-4 h-4" />;
      case "in_progress":
        return <AlertCircle className="w-4 h-4" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "text-blue-600 dark:text-blue-400";
      case "in_progress":
        return "text-yellow-600 dark:text-yellow-400";
      case "resolved":
        return "text-green-600 dark:text-green-400";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MessageSquare className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Support Tickets</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Get help with your account and card issues
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "new" ? "default" : "outline"}
            onClick={() => setActiveTab("new")}
            className="flex-1"
          >
            New Ticket
          </Button>
          <Button
            variant={activeTab === "existing" ? "default" : "outline"}
            onClick={() => setActiveTab("existing")}
            className="flex-1"
          >
            My Tickets
          </Button>
        </div>

        {/* New Ticket Form */}
        {activeTab === "new" && (
          <Card className="bg-card/80">
            <CardContent className="p-6 space-y-4">
              {/* Category */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Category <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleInputChange("category", value)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {ticketCategories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Subject <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Brief description of your issue"
                  value={formData.subject}
                  onChange={(e) => handleInputChange("subject", e.target.value)}
                  className="h-12"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Description <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Please provide detailed information about your issue..."
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="min-h-[120px] resize-none"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Email (Optional)
                </label>
                <Input
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  We'll send updates about your ticket to this email
                </p>
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Attachments</label>
                
                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                    isDragging 
                      ? "border-primary bg-primary/5" 
                      : "border-muted-foreground/25 hover:border-muted-foreground/50"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                  />
                  
                  <div className="flex flex-col items-center justify-center text-center">
                    <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium mb-1">
                      Drag & drop files here
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      or
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Browse Files
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Supports: JPG, PNG, GIF, WebP (Max 10MB each)
                    </p>
                  </div>
                </div>

                {/* File List */}
                {formData.attachments.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {formData.attachments.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {getFileIcon(file.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      {formData.attachments.length} file(s) selected
                    </p>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                className="w-full h-12"
                disabled={!formData.category || !formData.subject || !formData.description}
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Ticket
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Existing Tickets */}
        {activeTab === "existing" && (
          <div className="space-y-3">
            {existingTickets.length === 0 ? (
              <Card className="bg-card/80">
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No tickets yet</p>
                  <Button
                    onClick={() => setActiveTab("new")}
                    className="mt-4"
                  >
                    Create New Ticket
                  </Button>
                </CardContent>
              </Card>
            ) : (
              existingTickets.map((ticket) => (
                <Card
                  key={ticket.id}
                  className="bg-card/80 hover:bg-card/90 transition-colors cursor-pointer"
                  onClick={() => {
                    // Navigate to ticket detail page
                    navigate(`/support-ticket/${ticket.id}`);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-muted-foreground">
                            {ticket.id}
                          </span>
                          <span
                            className={`text-xs font-semibold ${
                              priorityLevels.find(
                                (p) => p.value === ticket.priority
                              )?.color
                            }`}
                          >
                            {ticket.priority.toUpperCase()}
                          </span>
                        </div>
                        <h3 className="font-semibold text-sm mb-1">
                          {ticket.subject}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {ticket.category} • {ticket.date}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/20">
                      <div className={`flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                        {getStatusIcon(ticket.status)}
                        <span className="text-xs font-medium">
                          {ticket.status.replace("_", " ").toUpperCase()}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Updated {ticket.lastUpdate}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      <BottomBar />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4">
          <div className="bg-background rounded-[20px] shadow-lg p-6 w-full max-w-[360px]">
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="p-1 hover:bg-muted rounded-full"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold">Ticket Submitted!</h3>
              <p className="text-sm text-muted-foreground">
                Your support ticket has been created successfully. We'll respond within 24 hours.
              </p>
              <p className="text-sm font-semibold">
                Ticket ID: TKT-2024-{Math.floor(Math.random() * 1000).toString().padStart(3, '0')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}